import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { Calendar, FileCode, Trash2, Eye } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export interface Repository {
  id: string;
  name: string;
  createdAt: string;
  fileCount: number;
  analysisStatus: 'pending' | 'analyzing' | 'complete' | 'failed';
}

interface RepositoryCardProps {
  repository: Repository;
  onDelete: (id: string) => void;
}

export function RepositoryCard({
  repository,
  onDelete,
}: RepositoryCardProps): React.JSX.Element {
  const getStatusBadge = (status: Repository['analysisStatus']): React.JSX.Element => {
    switch (status) {
      case 'complete':
        return <Badge variant="success">Complete</Badge>;
      case 'analyzing':
        return <Badge variant="warning">Analyzing</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="secondary">Pending</Badge>;
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Card className="flex flex-col hover:shadow-lg transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <CardTitle className="truncate">{repository.name}</CardTitle>
            <CardDescription className="flex items-center gap-1 mt-1">
              <Calendar className="h-3 w-3" />
              {formatDate(repository.createdAt)}
            </CardDescription>
          </div>
          {getStatusBadge(repository.analysisStatus)}
        </div>
      </CardHeader>

      <CardContent className="flex-1">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <FileCode className="h-4 w-4" />
          <span>{repository.fileCount} files</span>
        </div>
      </CardContent>

      <CardFooter className="flex gap-2 pt-4 border-t">
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          asChild
        >
          <Link
            to="/repositories/$repoId"
            params={{ repoId: repository.id }}
          >
            <Eye className="h-4 w-4 mr-1" />
            View
          </Link>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onDelete(repository.id)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
}
